// @givmo/passkeys — the organization door's routes on the web (AS-WEB30): the backend's employer-SSO
// ceremony (AS-BE13) and the signed-out read that decides whether a page draws the door at all.
//
//   - `GET /authn/sso/availability?redirect_uri=` → `{available}`: whether company sign-in can run
//     for THIS console here — the routes served, and this console's callback on this environment's
//     allowlist (the same predicate `/route` applies). Reads no account and spends no sign-in attempt;
//     sized for page loads.
//   - `POST /authn/sso/route` `{email, code_challenge, redirect_uri}` → `{sso_available,
//     authorize_url, state}`. Uniform: no account is read; `false` covers no company, below the SSO
//     tiers and an unverified domain alike. `redirect_uri` must match the allowlist exactly, else 400
//     `SSO_REDIRECT_URI_NOT_ALLOWED` (or `SSO_NOT_CONFIGURED` when the list is unset).
//   - The callback answers a 303 to this console's callback page with `handoff_code` (one use, two
//     minutes) or `error` (`SSO_IDENTITY_NOT_LINKED`, else `SSO_CEREMONY_FAILED`).
//   - `POST /authn/sso/token` `{handoff_code, code_verifier}` → `{custom_token}`; one uniform 400
//     `SSO_CEREMONY_FAILED`.
//   - Everything: the absent-route 404 while `EMPLOYER_SSO_ENABLED` is off; 429 per IP.
//
// ⚠ NOT IN THE COMMITTED SNAPSHOT (the `/authn` sub-app), so the sanctioned `givmoClient` calls, as
// the passkey sign-in's transport. ⚠ THE EXCHANGE'S SUCCESS BODY IS A BEARER CREDENTIAL: returned to
// the caller and nothing else (`passkey-assertion-transport.ts`' rule).

import { givmoClient, isGivmoError } from '@givmo/api-client';
import type { OrganizationSsoTransport } from '@givmo/auth';
import { extractCustomToken } from './passkey-transports';

const ROUTE_URL = '/authn/sso/route';
const TOKEN_URL = '/authn/sso/token';
const AVAILABILITY_URL = '/authn/sso/availability';

/** The exchange answered 200 with no token this client can read. */
export class OrganizationSsoUnreadableError extends Error {
  constructor() {
    super('Company sign-in answered with a body this client cannot read.');
    this.name = 'OrganizationSsoUnreadableError';
    Object.setPrototypeOf(this, OrganizationSsoUnreadableError.prototype);
  }
}

/** The transport `@givmo/auth`'s `signInWithOrganizationSso` rides on, bound to the shared client. */
export function createOrganizationSsoTransport(): OrganizationSsoTransport {
  return {
    async route({ email, codeChallenge, redirectUri }) {
      const { data, error } = await givmoClient.post({
        url: ROUTE_URL,
        body: { email, code_challenge: codeChallenge, redirect_uri: redirectUri },
      });
      if (error !== undefined && error !== null) throw error;
      const body = (data ?? {}) as { sso_available?: unknown; authorize_url?: unknown };
      const authorizeUrl =
        typeof body.authorize_url === 'string' && body.authorize_url.length > 0
          ? body.authorize_url
          : null;
      return { ssoAvailable: body.sso_available === true && authorizeUrl !== null, authorizeUrl };
    },

    async exchange({ handoffCode, codeVerifier }) {
      const { data, error } = await givmoClient.post({
        url: TOKEN_URL,
        body: { handoff_code: handoffCode, code_verifier: codeVerifier },
      });
      if (error !== undefined && error !== null) throw error;
      const customToken = extractCustomToken(data);
      if (customToken === null) throw new OrganizationSsoUnreadableError();
      return customToken;
    },
  };
}

/** What a failure means to the person: one of the door's states (frames WF1–WF5). */
export type OrganizationSsoFailure =
  | 'noSso'
  | 'didNotFinish'
  | 'notLinked'
  | 'notHere'
  | 'throttled';

/** The callback's `error` query value, or a refused call's machine code. */
export function organizationSsoFailureOfCode(
  code: string | null | undefined,
): OrganizationSsoFailure {
  if (code === 'SSO_IDENTITY_NOT_LINKED') return 'notLinked';
  if (code === 'SSO_REDIRECT_URI_NOT_ALLOWED' || code === 'SSO_NOT_CONFIGURED') return 'notHere';
  return 'didNotFinish';
}

/** A thrown refusal from `route` or `exchange`, read as the door's state. */
export function organizationSsoFailureOf(thrown: unknown): OrganizationSsoFailure {
  if (isGivmoError(thrown)) {
    if (thrown.status === 429) return 'throttled';
    if (thrown.status === 404) return 'notHere';
    if (thrown.status === 400) return organizationSsoFailureOfCode(thrown.entries[0]?.errorCode);
  }
  return 'didNotFinish';
}

/**
 * What the availability read said, for the door's cache (`organization-sso-door.tsx`):
 *   - `available`   — the routes are served and this console's callback is allowlisted;
 *   - `unavailable` — not here (the dark 404, or `{available: false}`);
 *   - `unknown`     — a 429, a 5xx or no answer: says nothing about whether the door can work.
 * Never throws, and silent on every path: a dark 404 is the EXPECTED answer on most sign-in pages.
 */
export type OrganizationSsoAvailability = 'available' | 'unavailable' | 'unknown';

export async function readOrganizationSsoAvailability(
  redirectUri: string,
): Promise<OrganizationSsoAvailability> {
  try {
    const { data, error } = await givmoClient.get({
      url: AVAILABILITY_URL,
      query: { redirect_uri: redirectUri },
    });
    if (error !== undefined && error !== null) {
      return isGivmoError(error) && error.status === 404 ? 'unavailable' : 'unknown';
    }
    const available = (data as { available?: unknown } | undefined)?.available;
    if (available === true) return 'available';
    if (available === false) return 'unavailable';
    return 'unknown';
  } catch {
    return 'unknown';
  }
}
